import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useDispatch } from 'react-redux';
import { TablePendingUserListToManager } from './TablePendingUserListToManager';
import { TableUserListToManage } from './TableUserListToManage';
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Col, Input, Row, Space } from 'antd';
import { useStore } from '../../root-store-context';
import Headers from '../../../../../../../redux/Actions/Headers/Header';

export const UserManagementTool = observer(() => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('pending');
    const debounceTimer = useRef(null);
    const [pendingSearchValue, setPendingSearchValue] = useState('');
    const [completedSearchValue, setCompletedSearchValue] = useState('');
    const [isSearchingPending, setIsSearchingPending] = useState(false);
    const [isSearchingCompleted, setIsSearchingCompleted] = useState(false);
    const {
        pendingUserListToManageStore: { getPendingUserListToManageAction },
        userListToManageStore: { getUserListToManageAction },
    } = useStore();

    useEffect(() => {
        dispatch(
            Headers.setHeader({
                title: 'All Access Admin Tool',
                actions: [],
            }),
        );

        return () => {
            dispatch(Headers.resetHeader());
        };
    }, [dispatch]);

    const handleSearch = (value: string, tab: string) => {
        const searchValue = value.trim();
        if (tab === 'pending') {
            setIsSearchingPending(true);
            getPendingUserListToManageAction(searchValue, 0).finally(() => {
                setIsSearchingPending(false);
            });
            return;
        }

        setIsSearchingCompleted(true);
        getUserListToManageAction(searchValue, 0).finally(() => {
            setIsSearchingCompleted(false);
        });
    };

    const handleSearchInputChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const { value } = event.target;

        if (activeTab === 'pending') {
            setPendingSearchValue(value);
        } else {
            setCompletedSearchValue(value);
        }

        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        debounceTimer.current = setTimeout(() => {
            handleSearch(value, activeTab);
        }, 700);
    };

    const handleTabChange = (key: string) => {
        setActiveTab(key);
        setPendingSearchValue('');
        setCompletedSearchValue('');
        setIsSearchingPending(false);
        setIsSearchingCompleted(false);
    };

    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    const getTabButtonStyle = (key: string) => ({
        borderRadius: '6px',
        height: '32px',
        padding: '0 14px',
        border: '1px solid #6f88a0',
        backgroundColor: activeTab === key ? '#6f88a0' : '#ffffff',
        color: activeTab === key ? '#ffffff' : '#4f6b85',
        boxShadow: 'none',
    });

    return (
        <>
            <Space
                direction="vertical"
                style={{ width: '100%' }}
                size={[0, 10]}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Space size={15}>
                        <Button
                            type="default"
                            style={getTabButtonStyle('pending')}
                            onClick={() => handleTabChange('pending')}
                        >
                            Pending Registration
                        </Button>
                        <Button
                            type="default"
                            style={getTabButtonStyle('completed')}
                            onClick={() => handleTabChange('completed')}
                        >
                            Completed Registration
                        </Button>
                    </Space>
                    <Input
                        style={{ width: 300, borderRadius: 18 }}
                        placeholder={
                            activeTab === 'pending'
                                ? 'Search pending registrations'
                                : 'Search completed registrations'
                        }
                        allowClear
                        prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
                        suffix={
                            activeTab === 'pending'
                                ? isSearchingPending && <LoadingOutlined />
                                : isSearchingCompleted && <LoadingOutlined />
                        }
                        value={
                            activeTab === 'pending'
                                ? pendingSearchValue
                                : completedSearchValue
                        }
                        onChange={handleSearchInputChange}
                    />
                </div>
                <div style={{ marginBottom: 16, width: '100%' }}>
                    <Row>
                        <Col span={24}>
                            {activeTab === 'pending' ? (
                                <TablePendingUserListToManager />
                            ) : (
                                <TableUserListToManage />
                            )}
                        </Col>
                    </Row>
                </div>
            </Space>
        </>
    );
});
